import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { compact, find, flatMap, get, isEmpty, map, size, uniq } from 'es-toolkit/compat';
import React from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';

import { AnnotationAssignmentAutocomplete } from 'components/atoms/AnnotationAssignmentAutocomplete';
import { AnnotationAssignment } from 'interfaces/annotation';
import { AnnotationStatsTable } from './AnnotationStatsTable';
import { IFormValues } from './types';

interface Props {
  studyId: string;
  formMethods: UseFormReturn<Partial<IFormValues>, any>;
  annotationAssignments: AnnotationAssignment[];
  isLoading?: boolean;
}

export const SelectAnnotationAssignmentsStep: React.FC<Props> = ({
  formMethods,
  studyId,
  annotationAssignments,
  isLoading,
}) => {
  const {
    watch,
    control,
    formState: { errors },
  } = formMethods;

  const stainingMethod = watch('stainingMethod');
  const selectedAssignmentIds = watch('annotationAssignmentIds');
  const slidesInAnnotationAssignments = uniq(
    flatMap(
      selectedAssignmentIds,
      (annotationAssignmentId) => find(annotationAssignments, { annotationAssignmentId })?.slideIds || []
    )
  );

  return (
    <>
      <Grid item>
        <Controller
          control={control}
          name="annotationAssignmentIds"
          render={({ field: { onChange, value } }) => (
            <AnnotationAssignmentAutocomplete
              multiple
              skipProcedureParams
              annotationAssignmentOptions={annotationAssignments}
              annotationAssignmentToGroup={(annotationAssignment) =>
                annotationAssignment?.studyId === studyId ? 'Current Study' : 'Other Studies'
              }
              orderByParams={[
                [(annotationAssignment) => annotationAssignment?.studyId === studyId, 'createdAt'],
                ['desc', 'desc'],
              ]}
              stainingMethod={stainingMethod}
              selectedValue={compact(value || [])}
              onChange={(event, newValue) => {
                onChange(compact(map(newValue, (assignment) => assignment?.annotationAssignmentId)));
              }}
              disabled={!stainingMethod}
              disableCloseOnSelect
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={
                    <>
                      Select annotation assignments
                      {isLoading && <CircularProgress color="inherit" size={20} sx={{ mr: 1 }} />}
                    </>
                  }
                  error={!stainingMethod || Boolean(get(errors, 'annotationAssignmentIds'))}
                  helperText={
                    !stainingMethod
                      ? 'Please select a staining method first'
                      : get(errors, 'annotationAssignmentIds')?.message
                  }
                />
              )}
            />
          )}
        />
      </Grid>
      {!isEmpty(selectedAssignmentIds) && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="caption">
              Slides in selected annotation assignments: {size(slidesInAnnotationAssignments)}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>Annotation Assignments Summary</AccordionSummary>
              <AccordionDetails>
                <AnnotationStatsTable
                  annotationAssignments={annotationAssignments}
                  selectedAssignmentIds={selectedAssignmentIds}
                />
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      )}
    </>
  );
};
